import React, { useCallback, useRef, useState } from 'react';
import { usePopoverDismissOutside } from './usePopoverDismissOutside';
import {
  arkoContextHoverLabel,
  formatArkoTokenCount,
  type ArkoContextCategory,
  type ArkoContextUsage,
} from './arkoContextWindow';

const CATEGORY_COLOR: Record<string, string> = {
  system: '#94a3b8',
  intent: '#c4b5fd',
  handoff: '#f9a8d4',
  extracts: '#a78bfa',
  history: '#7dd3fc',
  lexcore: '#6ee7b7',
  memcore: '#fcd34d',
  web: '#38bdf8',
  draft: '#f0abfc',
};

function fillColor(percent: number): string {
  if (percent >= 90) return '#fb7185';
  if (percent >= 70) return '#fbbf24';
  return '#a78bfa';
}

const SIZE = 18;
const STROKE = 2.25;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export const ArkoContextMeter: React.FC<{ usage: ArkoContextUsage }> = ({ usage }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const pctFill = Math.max(0, Math.min(100, usage.percent));
  const dash = (pctFill / 100) * CIRCUMFERENCE;
  const hover = arkoContextHoverLabel(usage);
  const stroke = fillColor(usage.percent);
  const totalTokens = Math.max(usage.usedTokens, 1);

  usePopoverDismissOutside({ open, onClose: close, rootRef });

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        className="portal-arko-context-meter flex items-center justify-center rounded-full p-0.5 text-slate-500 hover:text-slate-200 focus:outline-none focus-visible:ring-1 focus-visible:ring-violet-400/40"
        aria-label={`Kontextfenster ${hover}`}
        title={hover}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
      >
        <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden>
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke="rgba(255,255,255,0.12)"
            strokeWidth={STROKE}
          />
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke={stroke}
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${CIRCUMFERENCE}`}
            transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
          />
        </svg>
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Kontext"
          className="absolute bottom-full right-0 mb-2 z-40 w-[min(100vw-2rem,18.5rem)] rounded-xl border border-white/[0.1] bg-[#141619] p-3 shadow-[0_-8px_32px_rgba(0,0,0,0.45)]"
        >
          <div className="flex items-baseline justify-between gap-2 mb-2">
            <h2 className="text-[11px] font-medium uppercase tracking-wider text-slate-400">
              Kontext
            </h2>
            <p className="text-[11px] text-slate-300 tabular-nums">{hover}</p>
          </div>

          <div
            className="flex h-1.5 w-full overflow-hidden rounded-full bg-white/[0.06] mb-3"
            aria-hidden
          >
            {usage.categories.map((cat) => (
              <span
                key={cat.id}
                className="h-full"
                style={{
                  width: `${(cat.tokens / totalTokens) * 100}%`,
                  backgroundColor: CATEGORY_COLOR[cat.id] || '#64748b',
                }}
              />
            ))}
          </div>

          <ul className="space-y-1.5">
            {usage.categories.map((cat) => (
              <LegendRow key={cat.id} category={cat} />
            ))}
          </ul>

          <p className="mt-2.5 text-[10px] leading-snug text-slate-600">
            Näherung des aktuellen Auftrags. Kein internes Modell-Detail.
          </p>
        </div>
      ) : null}
    </div>
  );
};

const LegendRow: React.FC<{ category: ArkoContextCategory }> = ({ category }) => (
  <li className="flex items-start gap-2 text-[11px] text-slate-300">
    <span
      className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
      style={{ backgroundColor: CATEGORY_COLOR[category.id] || '#64748b' }}
      aria-hidden
    />
    <span className="min-w-0 flex-1">
      <span className="flex items-baseline justify-between gap-2">
        <span className="truncate">{category.label}</span>
        <span className="shrink-0 tabular-nums text-slate-500">
          ~{formatArkoTokenCount(category.tokens)}
        </span>
      </span>
      {category.detail ? (
        <span className="block truncate text-[10px] text-slate-600" title={category.detail}>
          {category.detail}
        </span>
      ) : null}
    </span>
  </li>
);
